import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchUserProfile = useCallback(async (currentToken) => {
        if (!currentToken) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/profile`, {
                headers: { 'Authorization': `Bearer ${currentToken}` }
            });
            const data = await response.json();
            if (data.success && data.user) {
                // Combine with existing decoded user data to keep all claims
                setUser(prev => ({ ...prev, ...data.user }));
            }
        } catch (err) {
            console.error("Failed to fetch fresh profile:", err);
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    }, []);

    useEffect(() => {
        const initializeAuth = async () => {
            if (token) {
                try {
                    const decodedUser = jwtDecode(token);
                    // Check if token is expired
                    if (decodedUser.exp * 1000 < Date.now()) {
                        logout();
                    } else {
                        setUser({ ...decodedUser, token }); // Set initial user from token
                        fetchUserProfile(token); // Then fetch the full profile
                    }
                } catch (error) {
                    console.error("Invalid token:", error);
                    logout();
                }
            }
            setLoading(false);
        };

        initializeAuth();
    }, [token, logout, fetchUserProfile]);

    const login = useCallback(async (email, code) => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code }),
        });

        const data = await response.json();

        if (data.success && data.token) {
            localStorage.setItem('token', data.token);
            setToken(data.token);
            return { success: true };
        } else {
            return { success: false, error: data.error || 'Login failed.' };
        }
    }, []);

    const setAuthToken = useCallback((newToken) => {
        if (newToken) {
            localStorage.setItem('token', newToken);
            setToken(newToken);
        }
    }, []);

    const requestLoginCode = useCallback(async (email) => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/request-login-code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        return response.json();
    }, []);

    const fetchOrders = useCallback(async () => {
        if (!token) return { success: false, error: 'Not authenticated' };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errData = await response.json();
            return { success: false, error: errData.error || 'Failed to fetch orders.' };
        }

        return response.json();
    }, [token]);

    const uploadFileForOrder = useCallback(async (orderId, uploadType, file, onProgress) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        return new Promise((resolve) => {
            const xhr = new XMLHttpRequest();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('uploadType', uploadType);

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && onProgress) {
                    const percent = (e.loaded / e.total) * 100;
                    onProgress(percent);
                }
            });

            xhr.addEventListener('load', () => {
                try {
                    const response = JSON.parse(xhr.responseText);
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(response);
                    } else {
                        resolve({ success: false, error: response.error || 'Upload failed.' });
                    }
                } catch (e) {
                    resolve({ success: false, error: 'Server error during upload.' });
                }
            });

            xhr.addEventListener('error', () => {
                resolve({ success: false, error: 'Network error.' });
            });

            xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/upload`);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.send(formData);
        });
    }, [token]);

    const submitDocumentsForOrder = useCallback(async (orderId, data = {}) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data)
        });

        return response.json();
    }, [token]);

    const removeFileForOrder = useCallback(async (orderId, uploadType) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/upload/${uploadType}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        return response.json();
    }, [token]);

    // Uploads a file for a specific questionnaire question (e.g. "attach your
    // HIV test result") rather than one of the fixed id/video/evidence slots.
    const uploadQuestionnaireFile = useCallback(async (orderId, questionName, file, onProgress) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        return new Promise((resolve) => {
            const xhr = new XMLHttpRequest();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('questionName', questionName);

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && onProgress) {
                    const percent = (e.loaded / e.total) * 100;
                    onProgress(percent);
                }
            });

            xhr.addEventListener('load', () => {
                try {
                    const response = JSON.parse(xhr.responseText);
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(response);
                    } else {
                        resolve({ success: false, error: response.error || 'Upload failed.' });
                    }
                } catch (e) {
                    resolve({ success: false, error: 'Server error during upload.' });
                }
            });

            xhr.addEventListener('error', () => {
                resolve({ success: false, error: 'Network error.' });
            });

            xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/questionnaire/upload`);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.send(formData);
        });
    }, [token]);

    const removeQuestionnaireFile = useCallback(async (orderId, questionName) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/questionnaire/upload/${questionName}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        return response.json();
    }, [token]);

    const cancelOrderReview = useCallback(async (orderId) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/cancel-review`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        return response.json();
    }, [token]);

    const getLetterDownloadUrl = useCallback(async (orderId) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/letter-url`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        return response.json();
    }, [token]);

    const updateUserDetails = useCallback(async (details) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/profile`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(details),
        });

        if (!response.ok) {
            let errorMessage = `Failed to update details. Status: ${response.status}.`;
            try {
                // Try to get a more specific error message from the backend response
                const errData = await response.json();
                errorMessage = errData.error || errorMessage;
            } catch (e) {
                // Response was not JSON, use the status text.
                errorMessage = response.statusText || errorMessage;
            }
            return { success: false, error: errorMessage };
        }

        const data = await response.json(); // This should now succeed

        if (data.success && data.token) {
            // If the backend sends back a new token with updated claims, save it.
            localStorage.setItem('token', data.token);
            setToken(data.token);
            return { success: true, token: data.token };
        } else {
            return { success: false, error: data.error || 'Failed to update details.' };
        }
    }, [token, setToken]);

    const saveQuestionnaireAnswers = useCallback(async (orderId, answers) => {
        if (!token) return { success: false, error: 'Not authenticated' };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/questionnaire`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ answers }),
        });

        const result = await response.json();
        if (result.success) {
            // After successfully saving, refresh the user profile to get updated order status
            await fetchUserProfile(token);
        }
        return result;
    }, [token, fetchUserProfile]);



    const value = { token, user, isAuthenticated: !!token, loading, login, logout, setAuthToken, requestLoginCode, fetchOrders, uploadFileForOrder, submitDocumentsForOrder, removeFileForOrder, cancelOrderReview, updateUserDetails, getLetterDownloadUrl, saveQuestionnaireAnswers, fetchUserProfile, uploadQuestionnaireFile, removeQuestionnaireFile };

    return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};